import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import uuid from "react-uuid";
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MarkerType,
  useEdgesState,
  useNodesState,
} from "reactflow";
import "reactflow/dist/style.css";

import FlowContext from "../../contexts/flowContext";
import SidebarContext from "../../contexts/sidebar";
import CopyPasteNodes from "../Home/Controls/CopyPasteNodes";
import AddNode from "./AddNode";
import ConditionalEdge from "./ConditionalEdge";
import style from "./flow.module.css";
import Node from "./Node";

const Flow = () => {
  const reactFlowWrapper = useRef(null);
  const [, dispatchSidebar] = useContext(SidebarContext);
  const [flowState, flowDispatcher] = useContext(FlowContext);

  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [selectableElement, setSelectableElement] = useState(false);

  const [nodes, setNodes, onNodesChange] = useNodesState(flowState.nodes);

  const [edges, setEdges, onEdgesChange] = useEdgesState(flowState.edges);

  const nodeTypes = useMemo(() => ({ basic: Node }), []);
  const edgeTypes = useMemo(() => ({ conditionalEdge: ConditionalEdge }), []);

  // activate selection if MAJ key clicked
  /*useEffect(() => {

        if (reactFlowInstance) {
            document.addEventListener('keydown', handleKeyDown)
            document.addEventListener('keyup', handleKeyUp)
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('keyup', handleKeyUp)
        }

        function handleKeyDown(event) {
            if (event.key === 'Shift') {
                setSelectableElement(true)
            }
        }

        function handleKeyUp(event) {
            if (event.key === 'Shift') {
                setSelectableElement(false)
            }
        }

    }, [reactFlowInstance])*/

  // Update viewport according to saved flow viewport
  useEffect(() => {
    if (flowState.viewport) {
      //reactFlowInstance.setViewport(flowState.viewport);
    }
  }, [flowState.viewport]);

  // Update rf instance on flowState
  useEffect(() => {
    if (reactFlowInstance) {
      flowDispatcher({
        type: "update-rf-instance",
        rfInstance: reactFlowInstance,
      });
    }
  }, [reactFlowInstance, flowDispatcher]);

  // Update visual flow nodes when flow state get changed
  useEffect(() => {
    if (flowState.nodes) {
      setNodes(flowState.nodes);
    }
  }, [flowState.nodes]);

  // Update visual flow edges when flow state get changed
  useEffect(() => {
    if (
      flowState.edges &&
      JSON.stringify(flowState.edges) !== JSON.stringify(edges)
    ) {
      setEdges(flowState.edges);
    }
  }, [flowState.edges]);

  // Update nodes list on context after adding/removing new node
  useEffect(() => {
    if (nodes.length > 0) {
      flowDispatcher({
        type: "update-node-list",
        value: nodes,
      });
    } else {
      flowDispatcher({
        type: "update-node-list",
        value: [],
      });
    }
  }, [nodes.length, flowDispatcher]);

  // Update edges list on context after adding/removing new edge
  useEffect(() => {
    if (edges.length > 0) {
      flowDispatcher({
        type: "update-edge-list",
        value: edges,
      });
    } else {
      flowDispatcher({
        type: "update-edge-list",
        value: [],
      });
    }
    /*flowDispatcher({
            type: 'order-next-node-list-conditions',
            list: [
                ...flowState.nodes.filter(node => node.id)[0].nextNodeListConditions,
                `reactflow__edge-${connection.source}-${connection.target}`
            ],
            nodeId: connection.source
        })*/
    /*
            TODO
             - ajouter une action dans le reducer flow
             - filterer le noeud dont la id est la source de l' edge ajouté
             - mettre à jour la liste de nextNodeListConditions
             - Ajouter ou supprimer le edge dans la liste de nextNodeListConditions
        */
  }, [edges.length]);

  /* useEffect(() => {
    if (JSON.stringify(flowState.edges) !== JSON.stringify(edges)) {
      flowDispatcher({
        type: "update-edge-list",
        value: edges,
      });
    }
  }, [edges]); */

  const handleNodeDoubleClick = function (event, node) {
    event.preventDefault();
    dispatchSidebar({
      type: "open",
      value: true,
      manager: "node",
    });
    flowDispatcher({
      type: "unselect-all-edges",
    });
    flowDispatcher({
      type: "set-selected-node",
      node: node,
    });
  };

  const handleNodeClick = useCallback(
    (event, node) => {
      flowDispatcher({
        type: "unselect-all-nodes",
      });
    },
    [flowState.nodes]
  );

  const handleNodeDelete = function (deletedNodes) {};

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData("application/reactflow");

      // check if the dropped element is valid
      if (typeof type === "undefined" || !type) {
        return;
      }

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });
      const newNode = {
        id: uuid(),
        type: "basic",
        position,
        data: {
          handles: 2, //nodes.filter(node => node.data.handles === 1).length? 2: 1,
          name: `Noeud-${uuid().split("-")[0]}`,
          module: {
            options: {
              type: "default",
              //description: "Description du noeud",
            },
            parameters: {},
          },
          starter: !nodes.length,
        },
        nextNodeListConditions: [],
      };

      // update node on for the visual
      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, nodes]
  );

  const handleNodeDragStop = useCallback(
    (event, node) => {
      const singleSelection =
        flowState.nodes.filter((node) => node.selected === true).length <= 1;

      if (singleSelection) {
        flowDispatcher({
          type: "update-node-position",
          value: node,
        });
      }
    },
    [flowDispatcher, flowState.nodes]
  );

  const handleNodeDragStart = useCallback(() => {
    flowDispatcher({
      type: "unselect-all-nodes",
    });
  }, [flowDispatcher]);

  const handleSelectionDragStop = useCallback(
    (event, nodes) => {},
    [flowDispatcher]
  );

  const handlePaneClick = function (e) {
    nodes.forEach((node) => {
      if (node.selected) {
        flowDispatcher({
          type: "update-node-position",
          value: node,
        });
      }
    });
    flowDispatcher({
      type: "unselect-all-edges",
    });
    flowDispatcher({
      type: "unselect-all-nodes",
    });
  };

  //Edge handlers
  const onConnect = useCallback(
    (connection) => {
      const edgeId = `reactflow__edge-${connection.source}-${connection.target}`;
      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            //animated: true,
            id: edgeId,
            type: "conditionalEdge",
            label: "unconditional",
            /*labelBgPadding: [8, 4],
            labelBgBorderRadius: 4,
            labelBgStyle: {fill: '#FFCC00', color: '#fff', fillOpacity: 0.7},
            labelStyle: {
                fontSize: 15
            },*/
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: "#f6003c",
            },
            style: {
              stroke: "#f6003c",
              strokeWidth: 2,
              strokeOpacity: 0.7,
            },
            data: {
              condition: "",
            },
          },
          eds
        )
      );

      flowDispatcher({
        type: "add-to-next-condition-list",
        nodeId: connection.source,
        edgeId,
      });
    },
    [setEdges]
  );

  const handleEdgeDelete = function (edges) {
    setTimeout(() => {
      edges.forEach((edge) => {
        flowDispatcher({
          type: "remove-next-condition-from-list",
          nodeId: edge.source,
          edgeId: edge.id,
        });
      });
    }, 300);
  };

  const handleMouseEnterPane = function () {
    /*flowDispatcher({
            type: 'mouse-over-pane'
        })*/
  };

  const handleMouseLeavePane = function () {
    /*flowDispatcher({
            type: 'mouse-outside-pane'
        })*/
  };

  const handleEdgeClick = function (event, edge) {
    dispatchSidebar({
      type: "open",
      value: true,
      manager: "edge",
    });
    flowDispatcher({
      type: "unselect-all-nodes",
    });
    flowDispatcher({
      type: "set-selected-edge",
      id: edge.id,
    });
  };

  /*const handleNodeMouseEnter = function () {
        setSelectableElement(false)
    }
    const handleNodeMouseLeave = function () {
        setSelectableElement(true)
    }*/

  const onEdgeUpdate = useCallback((oldEdge, newConnection) => {
    flowDispatcher({
      type: "update-edge-connection",
      id: oldEdge.id,
      value: {
        ...oldEdge,
        ...newConnection,
      },
    });
  });

  return (
    <div className={style.react_flow__container} ref={reactFlowWrapper}>
      <ReactFlow
        nodes={nodes}
        // TODO
        // Supprimer le map des edges. Fonction intégré temporairement pour fixe le
        // bug des sourceHandle des edges.
        // La fonction deviendra inutilie à partir du moment où tous les anciens flows auraient
        // été sauvegardés
        edges={edges.map((edge) => {
          return { ...edge, sourceHandle: "a" };
        })}
        //onNodeMouseEnter={handleNodeMouseEnter}
        //onNodeMouseLeave={handleNodeMouseLeave}
        //elementsSelectable={selectableElement}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={setReactFlowInstance}
        attributionPosition="top-right"
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onNodesDelete={handleNodeDelete}
        onNodeDragStart={handleNodeDragStart}
        onNodeDragStop={handleNodeDragStop}
        onSelectionDragStop={handleSelectionDragStop}
        onPaneClick={handlePaneClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onEdgeDoubleClick={handleEdgeClick}
        onMouseEnter={handleMouseEnterPane}
        onMouseLeave={handleMouseLeavePane}
        onEdgesDelete={handleEdgeDelete}
        onEdgeUpdate={onEdgeUpdate}
        onClick={() =>
          flowDispatcher({
            type: "disable-edition-in-progress",
          })
        }
        //fitView
      >
        <Controls />
        <CopyPasteNodes rfInstance={reactFlowInstance} />
        <Background gap={16} />
      </ReactFlow>
      <AddNode />
    </div>
  );
};

export default Flow;
